import { Mesh2MotionEngine } from './Mesh2MotionEngine'
import { ProcessStep } from './lib/enums/ProcessStep'
import { type SkeletonDescriptor } from './features/skeleton/SkeletonDescriptor'
import { ProjectSessionsPanel } from './features/persistence/ProjectSessionsPanel'
import { confirm_action } from './lib/ConfirmationDialog.ts'

export class CustomModelUploadBootstrap {
  private readonly mesh2motion_engine: Mesh2MotionEngine
  private sessions_panel: ProjectSessionsPanel | undefined

  constructor () {
    this.mesh2motion_engine = new Mesh2MotionEngine()
    this.wire_build_skeleton_events()
    this.wire_project_sessions_panel()
  }

  private wire_project_sessions_panel (): void {
    const init = (): void => {
      const panel = document.querySelector<HTMLDivElement>('#resume-project-panel')
      const list = document.querySelector<HTMLDivElement>('#resume-project-list')
      if (panel === null || list === null) return
      this.sessions_panel = new ProjectSessionsPanel({
        panel,
        list,
        on_resume: async (id) => {
          // Hide the panel immediately to avoid double-click resumes; the
          // engine drives the rest of the flow (load model, advance step).
          panel.style.display = 'none'
          const ok = await this.mesh2motion_engine.restore_session(id)
          if (!ok) {
            // Surface the panel again so the user can pick a different one.
            panel.style.display = 'flex'
            void this.sessions_panel?.refresh()
          }
        }
      })
      void this.sessions_panel.refresh()
      window.addEventListener('meshinator-project-session-imported', ((event: CustomEvent<{ session_id: string }>) => {
        void this.sessions_panel?.refresh()
        void confirm_action({
          title: 'Load imported mesh',
          message: 'Load the bundled mesh from this imported project now?',
          confirm_label: 'Load mesh'
        }).then(async (confirmed) => {
          if (!confirmed) return
          panel.style.display = 'none'
          const ok = await this.mesh2motion_engine.restore_session(event.detail.session_id)
          if (!ok) {
            panel.style.display = 'flex'
            void this.sessions_panel?.refresh()
          }
        })
      }) as EventListener)
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init)
    } else {
      init()
    }
  }

  private wire_build_skeleton_events (): void {
    // "+ Build New Skeleton" button on LoadSkeleton screen.
    this.mesh2motion_engine.load_skeleton_step.addEventListener('requestBuildSkeleton', () => {
      this.mesh2motion_engine.process_step_changed(ProcessStep.BuildSkeleton)
    })

    // After saving in Build Skeleton, return to LoadSkeleton with the new
    // custom skeleton already selected so the user can proceed straight to
    // EditSkeleton.
    this.mesh2motion_engine.addEventListener('build-skeleton-saved', ((e: CustomEvent<SkeletonDescriptor>) => {
      const descriptor = e.detail
      this.mesh2motion_engine.process_step_changed(ProcessStep.LoadSkeleton)
      void this.mesh2motion_engine.load_skeleton_step.select_custom_skeleton(descriptor.id)
    }) as EventListener)

    this.mesh2motion_engine.addEventListener('build-skeleton-cancelled', () => {
      this.mesh2motion_engine.process_step_changed(ProcessStep.LoadSkeleton)
    })
  }
}

// instantiate the class to setup event listeners
new CustomModelUploadBootstrap()
